import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ChevronLeft, MapPin, Navigation, Crosshair, Check, X } from 'react-feather';
import { RadiusConfig, PostcodeConfig, PolygonConfig } from '../types';
import { PolygonDrawingMap } from './PolygonDrawingMap';
import { RadiusPreviewMap } from './RadiusPreviewMap';
import './AddZoneView.css';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

interface GeocodingResult {
  id: string;
  place_name: string;
  center: [number, number];
}

interface AddZoneViewProps {
  onSave: (name: string, type: 'radius' | 'postcodes' | 'polygon', config: RadiusConfig | PostcodeConfig | PolygonConfig) => void;
  onBack: () => void;
  isFirstTime?: boolean;
}

type ZoneType = 'radius' | 'polygon';
type Step = 'type-selection' | 'configuration' | 'name';

// Reusable Stepper Component
const Stepper: React.FC<{ currentStep: number }> = ({ currentStep }) => (
  <div className="stepper">
    <div className={`stepper-step ${currentStep === 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
      <div className="stepper-dot">{currentStep > 1 ? <Check size={14} /> : '1'}</div>
      <span className="stepper-label">Welcome</span>
    </div>
    <div className={`stepper-line ${currentStep > 1 ? 'completed' : ''}`} />
    <div className={`stepper-step ${currentStep === 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
      <div className="stepper-dot">{currentStep > 2 ? <Check size={14} /> : '2'}</div>
      <span className="stepper-label">Zone</span>
    </div>
    <div className={`stepper-line ${currentStep > 2 ? 'completed' : ''}`} />
    <div className={`stepper-step ${currentStep === 3 ? 'active' : ''}`}>
      <div className="stepper-dot">3</div>
      <span className="stepper-label">Setup</span>
    </div>
  </div>
);

export const AddZoneView: React.FC<AddZoneViewProps> = ({ onSave, onBack, isFirstTime = false }) => {
  const [currentStep, setCurrentStep] = useState<Step>('type-selection');
  const [selectedType, setSelectedType] = useState<ZoneType | null>(null);
  const [name, setName] = useState('');
  
  // Radius config
  const [address, setAddress] = useState('');
  const [radius, setRadius] = useState(5);
  const [useCurrentLocation, setUseCurrentLocation] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
  
  // Address autocomplete
  const [addressSuggestions, setAddressSuggestions] = useState<GeocodingResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<GeocodingResult | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  
  // Polygon zone state
  const [polygonCoordinates, setPolygonCoordinates] = useState<number[][]>([]);
  const [isPolygonComplete, setIsPolygonComplete] = useState(false);

  // Only show Polygon and Radius zone types (removed Postcodes)
  const zoneTypes = [
    {
      id: 'polygon' as ZoneType,
      title: 'Custom Area',
      description: 'Draw a custom area on the map to monitor',
      icon: Navigation
    },
    {
      id: 'radius' as ZoneType,
      title: 'Radius Around Location',
      description: 'Monitor a circular area around an address or your current location',
      icon: MapPin
    }
  ];

  const handleTypeSelect = (type: ZoneType) => {
    setSelectedType(type);
    setCurrentStep('configuration');
  };

  const handleContinue = () => {
    if (!isConfigurationValid()) {
      return;
    }
    setCurrentStep('name');
  };

  const isConfigurationValid = useCallback(() => {
    if (selectedType === 'polygon') {
      return isPolygonComplete;
    }
    if (selectedType === 'radius') {
      // Must have coordinates from either selected address or current location
      return currentLocation !== null;
    }
    return false;
  }, [selectedType, isPolygonComplete, currentLocation]);

  const handlePolygonComplete = (coordinates: number[][]) => {
    setPolygonCoordinates(coordinates);
    setIsPolygonComplete(true);
  };

  const handleSave = () => {
    if (!name.trim() || !selectedType || !isConfigurationValid()) return;

    if (selectedType === 'radius') {
      if (!currentLocation) {
        alert('Location not available. Please select an address or use current location.');
        return;
      }
      
      const config: RadiusConfig = {
        centerLat: currentLocation.lat,
        centerLng: currentLocation.lng,
        radiusKm: radius,
        address: useCurrentLocation ? 'Current Location' : (selectedAddress?.place_name || address.trim())
      };
      
      onSave(name.trim(), 'radius', config);
    } else if (selectedType === 'polygon') {
      if (polygonCoordinates.length === 0) return;
      
      const lats = polygonCoordinates.map(coord => coord[1]);
      const lngs = polygonCoordinates.map(coord => coord[0]);
      
      const config: PolygonConfig = {
        points: polygonCoordinates.map(coord => ({ lat: coord[1], lng: coord[0] })),
        bounds: {
          north: Math.max(...lats),
          south: Math.min(...lats),
          east: Math.max(...lngs),
          west: Math.min(...lngs)
        }
      };
      onSave(name.trim(), 'polygon', config);
    }
  };

  const isSaveValid = () => {
    return name.trim() && selectedType && isConfigurationValid();
  };

  // Search for addresses using Mapbox Geocoding API
  const searchAddresses = useCallback(async (query: string) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&country=au&types=address,place,locality,neighborhood,poi&limit=5`
      );
      const data = await response.json();
      
      if (data.features) {
        setAddressSuggestions(data.features.map((f: any) => ({
          id: f.id,
          place_name: f.place_name,
          center: f.center
        })));
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      setAddressSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced address search
  const handleAddressChange = (value: string) => {
    setAddress(value);
    setUseCurrentLocation(false);
    setSelectedAddress(null);
    setCurrentLocation(null);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchAddresses(value);
    }, 300);
  };

  // Select an address from suggestions
  const handleSelectAddress = (suggestion: GeocodingResult) => {
    setAddress(suggestion.place_name);
    setSelectedAddress(suggestion);
    setCurrentLocation({
      lat: suggestion.center[1],
      lng: suggestion.center[0]
    });
    setShowSuggestions(false);
    setAddressSuggestions([]);
  };

  // Clear address
  const handleClearAddress = () => {
    setAddress('');
    setSelectedAddress(null);
    setCurrentLocation(null);
    setUseCurrentLocation(false);
    setAddressSuggestions([]);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const getCurrentLocation = () => {
    setUseCurrentLocation(true);
    setAddress('Getting current location...');
    setShowSuggestions(false);
    setAddressSuggestions([]);
    setSelectedAddress(null);
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setAddress('Current Location');
        },
        (error) => {
          console.log('Geolocation error:', error);
          setUseCurrentLocation(false);
          setCurrentLocation(null);
          setAddress('');
          alert('Could not get your location. Please enter an address manually or try again.');
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setUseCurrentLocation(false);
      setCurrentLocation(null);
      setAddress('');
      alert('Location services not available. Please enter an address manually.');
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const renderTypeSelection = () => (
    <div className="step-content">
      {isFirstTime && (
        <div className="onboarding-header">
          <Stepper currentStep={2} />
        </div>
      )}
      
      <div className="page-header">
        <button className="back-button-floating" onClick={onBack}>
          <ChevronLeft size={20} />
        </button>
        <div className="page-header-content">
          <h1>Add Alert Zone</h1>
          <p>Choose how to define your monitoring area</p>
        </div>
      </div>

      <div className="zone-types">
        {zoneTypes.map((type) => {
          const IconComponent = type.icon;
          return (
            <button
              key={type.id}
              className="zone-type-card"
              onClick={() => handleTypeSelect(type.id)}
            >
              <div className="zone-type-icon">
                <IconComponent size={24} />
              </div>
              <div className="zone-type-content">
                <h3>{type.title}</h3>
                <p>{type.description}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderConfiguration = () => {
    if (!selectedType) return null;

    return (
      <div className="step-content">
        {isFirstTime && (
          <div className="onboarding-header">
            <Stepper currentStep={2} />
          </div>
        )}
        
        <div className="page-header">
          <button className="back-button-floating" onClick={() => setCurrentStep('type-selection')}>
            <ChevronLeft size={20} />
          </button>
          <div className="page-header-content">
            <h1>Configure Zone</h1>
            <p>{selectedType === 'radius' ? 'Set your location and radius' : 'Draw your monitoring area'}</p>
          </div>
        </div>

        <div className="configuration-content">
          {selectedType === 'radius' && (
            <div className="config-section radius-config">
              {/* Location Input */}
              <div className="input-group">
                <label>Location</label>
                <div className="address-input-container">
                  <div className="address-input-wrapper">
                    <input
                      ref={inputRef}
                      type="text"
                      placeholder="Search for an address..."
                      value={address}
                      onChange={(e) => handleAddressChange(e.target.value)}
                      onFocus={() => address.length >= 3 && setShowSuggestions(true)}
                      disabled={useCurrentLocation}
                      className={selectedAddress || useCurrentLocation ? 'has-value' : ''}
                    />
                    {(address || useCurrentLocation) && (
                      <button className="clear-address-btn" onClick={handleClearAddress}>
                        <X size={16} />
                      </button>
                    )}
                    {isSearching && (
                      <div className="address-loading">
                        <div className="loading-spinner-small"></div>
                      </div>
                    )}
                  </div>
                  
                  {/* Address Suggestions Dropdown */}
                  {showSuggestions && addressSuggestions.length > 0 && (
                    <div className="address-suggestions" ref={suggestionsRef}>
                      {addressSuggestions.map((suggestion) => (
                        <button
                          key={suggestion.id}
                          className="suggestion-item"
                          onClick={() => handleSelectAddress(suggestion)}
                        >
                          <MapPin size={16} />
                          <span>{suggestion.place_name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Current Location Button */}
                <button 
                  className={`current-location-btn ${useCurrentLocation ? 'active' : ''}`}
                  onClick={getCurrentLocation}
                >
                  <Crosshair size={18} />
                  <span>{useCurrentLocation ? 'Using Current Location' : 'Use Current Location'}</span>
                </button>
              </div>

              {/* Radius Slider */}
              <div className="input-group">
                <label>Radius: <strong>{radius} km</strong></label>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={radius}
                  onChange={(e) => setRadius(parseInt(e.target.value))}
                  className="radius-slider"
                />
                <div className="slider-labels">
                  <span>1 km</span>
                  <span>50 km</span>
                </div>
              </div>

              {/* Map Preview */}
              <div className="input-group map-preview-group">
                <label>Preview</label>
                <RadiusPreviewMap 
                  center={currentLocation}
                  radiusKm={radius}
                  isLoading={isSearching}
                />
              </div>
            </div>
          )}

          {selectedType === 'polygon' && (
            <div className="config-section">
              <div className="polygon-drawing-section">
                <label>Draw Custom Area</label>
                <p className="section-help">
                  Tap on the map to place points and create your monitoring area.
                </p>
                <PolygonDrawingMap 
                  onPolygonComplete={handlePolygonComplete}
                />
              </div>
            </div>
          )}
        </div>

        <div className="action-footer">
          <button 
            className="continue-btn" 
            onClick={handleContinue}
            disabled={!isConfigurationValid()}
          >
            Continue
          </button>
        </div>
      </div>
    );
  };

  const renderNameStep = () => (
    <div className="step-content">
      {isFirstTime && (
        <div className="onboarding-header">
          <Stepper currentStep={2} />
        </div>
      )}
      
      <div className="page-header">
        <button className="back-button-floating" onClick={() => setCurrentStep('configuration')}>
          <ChevronLeft size={20} />
        </button>
        <div className="page-header-content">
          <h1>Name Your Zone</h1>
          <p>Give your alert zone a memorable name</p>
        </div>
      </div>

      <div className="name-content">
        <div className="input-group">
          <label>Zone Name</label>
          <input
            type="text"
            placeholder="e.g., Home Area, Work Commute"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </div>

        <div className="zone-summary">
          <h3>Summary</h3>
          <div className="summary-item">
            <span>Type</span>
            <span>{zoneTypes.find(t => t.id === selectedType)?.title}</span>
          </div>
          {selectedType === 'radius' && (
            <>
              <div className="summary-item">
                <span>Location</span>
                <span className="summary-address">{useCurrentLocation ? 'Current Location' : (selectedAddress?.place_name.split(',')[0] || 'Not set')}</span>
              </div>
              <div className="summary-item">
                <span>Radius</span>
                <span>{radius} km</span>
              </div>
            </>
          )}
          {selectedType === 'polygon' && (
            <div className="summary-item">
              <span>Area</span>
              <span>{isPolygonComplete ? `${polygonCoordinates.length - 1} points` : 'Not drawn'}</span>
            </div>
          )}
        </div>
      </div>

      <div className="action-footer">
        <button 
          className="save-btn"
          onClick={handleSave}
          disabled={!isSaveValid()}
        >
          Add Zone
        </button>
      </div>
    </div>
  );

  switch (currentStep) {
    case 'type-selection':
      return renderTypeSelection();
    case 'configuration':
      return renderConfiguration();
    case 'name':
      return renderNameStep();
    default:
      return renderTypeSelection();
  }
};
